import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import Dice from './Dice';
import { FaTrophy, FaHourglassHalf } from 'react-icons/fa';
import { Player, PlayerHistory } from '../types';
import { applyDiceRules, calculateDiceSum } from '../gameLogic';
import { playSound } from '../Utils/soundUtil';
import { useSettings, SCORE_SIZE_PX } from '../contexts/SettingsContext';

interface Props {
  player: Player;
  onDiceValueChange: (diceIndex: number, value: number) => void;
  endTurn: () => void;
  toggleFreeze: (diceIndex: number) => void;
  soundEnabled: boolean;
  botDifficulty?: 'easy' | 'medium' | 'hard';
}

const Card = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isCurrent' && prop !== 'isBot',
})<{ isCurrent: boolean; isBot: boolean }>`
  padding: ${({ theme }) => theme.spacing(2)};
  width: 90%;
  max-width: 320px;
  background: rgba(0, 43, 64, 0.5);
  border: ${({ isCurrent }) => (isCurrent ? '2px solid rgba(0,127,255,0.7)' : '1px solid rgba(255,255,255,0.12)')};
  border-radius: 10px;
  margin: 8px auto ${({ theme }) => theme.spacing(2)}; /* small top offset */
  position: relative;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.5);
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(0, 17, 26, 1), rgba(0, 17, 26, 0) 100%);
`;

const NameAndStatus = styled.div`
  display: flex;
  align-items: center;
  h2 {
    font-size: 16px;
    color: #fff;
    font-family: 'Roboto Condensed', sans-serif;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin: 0;
  }
`;

const HostIndicator = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.primary};
  margin-left: ${({ theme }) => theme.spacing(0.5)};
`;

const StatusBadge = styled.span<{ $isFinalThrow: boolean; $hasWon: boolean }>`
  background-color: ${({ $isFinalThrow, $hasWon, theme }) =>
    $hasWon
      ? 'linear-gradient(45deg, #ff9800, #ffc107)'  /* Golden, flashy gradient for winners */
      : $isFinalThrow
      ? 'linear-gradient(45deg, #ff5722, #ff9800)'  /* Final throw is bright and warm */
      : 'transparent'};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: ${({ theme }) => theme.spacing(0.5)} ${({ theme }) => theme.spacing(1)};
  border-radius: 6px;
  font-size: 16px;  /* Slightly larger for visibility */
  margin-left: ${({ theme }) => theme.spacing(1)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  text-shadow: ${({ $hasWon }) =>
    $hasWon ? '0 0 10px #ffeb3b, 0 0 20px #ff9800' : 'none'};  /* Glow for winners */
`;

const ScoreText = styled.strong.withConfig({
  shouldForwardProp: (prop) => prop !== 'isCurrent' && prop !== 'animatePop',
})<{ isCurrent: boolean; animatePop: boolean }>`
  font-size: 16px;
  font-family: 'Roboto Condensed', sans-serif;
  color: #fff;
  letter-spacing: 0.5px;
  transition: transform 0.3s ease, color 0.3s ease;
  ${({ animatePop }) =>
    animatePop &&
    css`
      animation: ${popShake} 0.5s ease-in-out;
    `}
`;

const popShake = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3) rotate(1deg);
  }
  60% {
    transform: scale(1.3) rotate(-1deg);
  }
  70% {
    transform: scale(1.2) rotate(1deg);
  }
`;

const DiceContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: ${({ theme }) => theme.spacing(2)};
  gap: ${({ theme }) => theme.spacing(1)};
`;

const EndTurnButtonContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  text-align: center;
`;

const EndTurnButton = styled.button`
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(3)};
  background: linear-gradient(180deg, rgb(0, 127, 255), rgb(0, 32, 64) 100%);
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 22px;
  font-family: 'Roboto Condensed', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.5);
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 127, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(0, 127, 255, 0.3);
  }
`;

const HistoryButtonContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  display: flex;
  justify-content: center;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const SwitchText = styled.span`
  font-family: 'Roboto Condensed', sans-serif;
  color: #ffffff;
  font-size: 14px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const SwitchTrack = styled.div<{ $on: boolean }>`
  width: 50px;
  height: 20px;
  border-radius: 12px;
  background: ${({ $on }) => ($on ? 'rgba(0,127,255,0.2)' : 'rgba(128,128,128,1)')};
  border: 1px solid rgba(128,128,128,0.6);
  display: flex;
  align-items: center;
  padding: 2px;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.5);
  position: relative;
  user-select: none;
`;

const SwitchThumb = styled.div<{ $on: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(255,255,255,0.85);
  border: 1px solid rgba(0,0,0,0.15);
  position: absolute;
  left: ${({ $on }) => ($on ? 'calc(100% - 18px)' : '2px')};
  transition: left 0.2s ease;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
`;

const HistoryContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  width: 280px;
  height: 100px;
  margin-left: auto;
  margin-right: auto;
  border-radius: 10px;
  background: rgba(0, 13, 26, 1);
  padding: 10px;
  box-sizing: border-box;
`;

const HistoryScroll = styled.div`
  width: 260px;
  height: 80px;
  max-height: 80px;
  overflow-y: auto;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: left;

  /* Custom scrollbar */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(255, 255, 255, 1) transparent; /* Firefox */

  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 1);
    border-radius: 5px;
    min-height: 60px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const HistoryItem = styled.div`
  margin-bottom: 4px;
  min-height: 19px;
  
  p {
    margin: 0;
    padding: 0;
    color: rgba(255, 255, 255, 1);
  }
`;

const FailureText = styled.span`
  color: rgba(255, 64, 0, 1);
  font-weight: 700;
`;

const SpecialEventText = styled.span`
  color: rgba(0, 191, 230, 1);
  font-weight: 700;
`;

const GainText = styled.span`
  color: rgba(0, 255, 85, 1);
  font-weight: 700;
`;

const BotLabel = styled.span`
  background: linear-gradient(90deg, #00e5ff, #00bcd4);
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  padding: 2px 10px;
  border-radius: 8px;
  margin-left: 10px;
  letter-spacing: 1px;
  box-shadow: 0 0 8px #00e5ff99;
`;

const BotDifficultyLabel = styled.div`
  background: linear-gradient(90deg, #00e5ff, #00bcd4);
  color: #fff;
  font-size: 13px;
  font-weight: bold;
  padding: 3px 14px;
  border-radius: 8px;
  margin-bottom: 8px;
  letter-spacing: 1px;
  box-shadow: 0 0 8px #00e5ff99;
  display: inline-block;
`;

// Helper function to animate score
const rollScore = (current: number, target: number): number => {
  const step = Math.sign(target - current) * Math.ceil(Math.abs(target - current) / 10); // Adjust the speed of the animation
  return current + step;
};

export type SoundKey =
| 'PISTE_1TO3'
| 'PISTE_4TO7'
| 'PISTE_8TO11'
| 'PISTE_12TO15'
| 'PISTE_16TO19'
| 'PISTE_20TO23'
| 'PISTE_24TO27'
| 'PISTE_28TO29'
| 'PISTE_30TO31';

const PlayerCard: React.FC<Props> = ({ player, onDiceValueChange, endTurn, toggleFreeze, soundEnabled, botDifficulty }) => {
  const { settings } = useSettings();
  const scoreFontSize = SCORE_SIZE_PX[settings.scoreSize];
  const [displayedScore, setDisplayedScore] = useState(player.score);
  const [showHistory, setShowHistory] = useState(false);
  const [animatePop, setAnimatePop] = useState(false);
  const [soundPlayed, setSoundPlayed] = useState(false); // Track if sound has been played for the current animation
  const [startingScore, setStartingScore] = useState(player.score); // Track the player's starting score for the turn

  const isBot = player.id === 'bot';
  // Detect if it's the bot's turn (bot is current player)
  const isBotTurn = isBot && player.isCurrent;

  // Bot dice spin step state
  const [botSpinStep, setBotSpinStep] = useState(0); // 0 = not spinning, 1 = first stops, 2 = second stops, etc.
  useEffect(() => {
    if (isBotTurn) {
      // Start spinning all dice when bot's turn starts
      setBotSpinStep(1);
    } else {
      setBotSpinStep(0);
    }
  }, [isBotTurn, player.dice]);

  // Step through dice stopping one by one
  useEffect(() => {
    if (isBotTurn && botSpinStep > 0 && botSpinStep <= player.dice.length) {
      const timer = setTimeout(() => {
        setBotSpinStep((step) => step + 1);
      }, 600); // Delay between each dice stopping
      return () => clearTimeout(timer);
    }
  }, [isBotTurn, botSpinStep, player.dice.length]);
  
  // Helper function to determine which sound to play based on the turn score
  const getSoundKeyBasedOnTurnScore = (turnScore: number): SoundKey | null => {
    console.log(`Determining sound key for turn score: ${turnScore}`); // Debugging remove later
  
    if (turnScore >= 1 && turnScore <= 3) {
      return 'PISTE_1TO3'; 
    } else if (turnScore >= 4 && turnScore <= 7) {
      return 'PISTE_4TO7'; 
    } else if (turnScore >= 8 && turnScore <= 11) {
      return 'PISTE_8TO11'; 
    } else if (turnScore >= 12 && turnScore <= 15) {
      return 'PISTE_12TO15'; 
    } else if (turnScore >= 16 && turnScore <= 19) {
      return 'PISTE_16TO19'; 
    } else if (turnScore >= 20 && turnScore <= 23) {
      return 'PISTE_20TO23'; 
    } else if (turnScore >= 24 && turnScore <= 27) {
      return 'PISTE_24TO27'; 
    } else if (turnScore >= 28 && turnScore <= 29) {
      return 'PISTE_28TO29'; 
    } else if (turnScore >= 30 && turnScore <= 31) {
      return 'PISTE_30TO31'; 
    }
    return null;
  };

  // Function to handle playing the correct sound based on the player's total turn score
  const handleCoinSound = useCallback(
    (currentDisplayedScore: number) => {
      // If the current displayed score is different from the starting score, update the starting score
      if (currentDisplayedScore !== startingScore) {
        setStartingScore(currentDisplayedScore);
      }
  
      const turnScore = player.score - startingScore; // Calculate the player's total turn score
      console.log(`Total turn score: ${turnScore}`); // Debugging remove later
  
      // Handle edge case for scoring exactly 1 point
      if (turnScore === 1) {
        playSound('PISTE_1TO3');
        setSoundPlayed(true); // Mark sound as played
        console.log('Playing isolated sound for scoring exactly 1 point');
        return;
      }
  
      const soundKey = getSoundKeyBasedOnTurnScore(turnScore);
  
      // If a valid sound key is found and the sound hasn't been played, play the sound
      if (soundKey && !soundPlayed) {
        playSound(soundKey);
        setSoundPlayed(true); // Mark sound as played
        console.log(`Playing sound for total turn score: ${soundKey}`);
      }
    },
    [startingScore, player.score, soundPlayed] // Dependencies for handleCoinSound
  );

  useEffect(() => {
    const targetScore = Math.min(player.score, 90);

    if (displayedScore !== targetScore) {
      // Reset the soundPlayed state when a new turn starts
      setSoundPlayed(false);

      // Handle special case where the player scores exactly 1 point with a separate animation
      if (targetScore - displayedScore === 1) {
        const interval = setInterval(() => {
          setDisplayedScore((prevScore) => {
            const newScore = prevScore + 1; // Increment slowly for 1-point score

            if (newScore === targetScore) {
              clearInterval(interval);
              handleCoinSound(newScore); // Play the sound specifically for 1-point increments
            }
            return newScore;
          });
        }, 150); // Slower interval for visual emphasis on the single point increment

        return () => clearInterval(interval);
      }

      // Normal interval for other scores
      const interval = setInterval(() => {
        setDisplayedScore((prevScore) => {
          const newScore = rollScore(prevScore, targetScore); // Cap the score at 90

          if (newScore === targetScore) {
            clearInterval(interval);
            handleCoinSound(newScore); // Pass the newScore to handleCoinSound for sound handling
          }
          return newScore;
        });
      }, 50); // Adjust speed by changing the interval timing

      // Cleanup the interval when effect dependencies change or component unmounts
      return () => clearInterval(interval);
    }
  }, [player.score, displayedScore, handleCoinSound]);


  useEffect(() => {
    // Trigger the pop animation and sound when the score reaches 90
    if (displayedScore === 90) {
      setAnimatePop(true);
      if (soundEnabled) {
        console.log("Playing Rikki sound");
        playSound('Rikki'); // Play the Rikki sound when the score reaches 90
      }
      // Reset the animation state after it's finished
      setTimeout(() => setAnimatePop(false), 500);
    }
  }, [displayedScore, soundEnabled]);

  const toggleHistory = () => {
    setShowHistory(!showHistory);
  };

  const getSpecialEventMessage = (entry: PlayerHistory) => {
    const { diceValues, isFrozen } = entry;
    const { score, outcome } = applyDiceRules(
      diceValues.map((value, index) => ({
        value,
        isFrozen: isFrozen[index],
      }))
    );
  
    // Miss condition
    if (outcome === 'miss') {
      return <FailureText>Miss!</FailureText>;
    }
  
    // Lost all points condition
    if (outcome === 'lose') {
      return <FailureText> (Lost all points)</FailureText>;
    }
  
    // Freeze condition
    if (outcome === 'freeze') {
      // Check if any points were doubled (this happens if frozen die has value 6 and 2 non-frozen dice)
      const frozenSix = diceValues.some((value, index) => isFrozen[index] && value === 6);
      const nonFrozenDiceCount = diceValues.filter((value, index) => !isFrozen[index] && value > 0).length;
  
      // Display different messages based on whether points were doubled or not
      if (frozenSix && nonFrozenDiceCount === 2) {
        return <SpecialEventText> (Freeze! - Frozen dice doubled)</SpecialEventText>;
      } else {
        return <SpecialEventText> (Freeze! - No points doubled)</SpecialEventText>; // No points doubled case
      }
    }
  
    // Tripla-Triplat condition
    if (score === 90 && outcome === 'continue') {
      return <SpecialEventText> (Tripla-Triplat!)</SpecialEventText>;
    }
  
    // Automatic Win condition (6,6,6)
    if (outcome === 'win') {
      return <SpecialEventText> (Automatic Win with 6,6,6!)</SpecialEventText>;
    }
  
    // Triplat condition (score doubled)
    if (score === calculateDiceSum(diceValues.map((value) => ({ value, isFrozen: false }))) * 2) {
      return <SpecialEventText> (Triplat!)</SpecialEventText>;
    }
  
    return null;
  };
  

  const renderHistoryContent = (entry: PlayerHistory, isFinalThrow: boolean) => {
    const specialMessage = getSpecialEventMessage(entry);
    const hasFailure = specialMessage && (specialMessage as any).type === FailureText;

    if (hasFailure) {
      // Show failure message (Miss or Lost all points)
      return <>{specialMessage}</>;
    }

    const gain = entry.turnScore > 0;
    return (
      <>
        {gain ? (
          <>
            Score <GainText>+ {entry.turnScore}</GainText>
          </>
        ) : (
          <>Score {entry.turnScore}</>
        )}
        {` `}[{entry.diceValues.join(', ')}]
        {isFinalThrow && <strong>{` `}(FinalThrow)</strong>}
        {specialMessage && <>{` `}{specialMessage}</>}
      </>
    );
  };
  
  return (
    <Card isCurrent={player.isCurrent} isBot={isBot}>
      {isBot && botDifficulty && (
        <BotDifficultyLabel>
          Bot Difficulty: {botDifficulty.charAt(0).toUpperCase() + botDifficulty.slice(1)}
        </BotDifficultyLabel>
      )}
      <Header>
        <NameAndStatus>
          <h2>
            {player.name}
            {isBot && <BotLabel>BOT</BotLabel>}
            {player.isHost && <HostIndicator>(Host)</HostIndicator>}
          </h2>
          {player.isFinalThrow && !player.hasWon && (
            <StatusBadge $isFinalThrow={true} $hasWon={false}>
              <FaTrophy />
              Final Throw
            </StatusBadge>
          )}
          {player.hasWon && (
            <StatusBadge $isFinalThrow={false} $hasWon={true}>
              <FaTrophy />
              Winner!
            </StatusBadge>
          )}
        </NameAndStatus>
        <ScoreText isCurrent={player.isCurrent} animatePop={animatePop} style={{ fontSize: scoreFontSize }}>SCORE: {displayedScore}</ScoreText>
      </Header>
  
      {player.isCurrent && (
        <>
          <DiceContainer>
            {player.dice.map((die, index) => (
              <Dice
                key={index}
                value={die.value}
                isFrozen={die.isFrozen}
                onToggleFreeze={() => toggleFreeze(index)}
                onChange={(value) => onDiceValueChange(index, value)}
                soundEnabled={soundEnabled}
                isBot={isBot}
                spinToValue={isBotTurn ? die.value : undefined}
                spinning={isBotTurn ? botSpinStep !== 0 && index >= botSpinStep - 1 : false}
              />
            ))}
          </DiceContainer>
          {!isBot && (
            <EndTurnButtonContainer>
              <EndTurnButton onClick={endTurn}>
                <FaHourglassHalf />
                End Turn
              </EndTurnButton>
            </EndTurnButtonContainer>
          )}
        </>
      )}
  
      <HistoryButtonContainer>
        <SwitchRow>
          <SwitchText>History</SwitchText>
          <div onClick={toggleHistory} style={{ cursor: 'pointer' }}>
            <SwitchTrack $on={showHistory}>
              <SwitchThumb $on={showHistory} />
            </SwitchTrack>
          </div>
        </SwitchRow>
      </HistoryButtonContainer>
  
      {showHistory && (
        <HistoryContainer>
          <HistoryScroll>
            {player.history.length === 0 ? (
              <p>No history yet.</p>
            ) : (
              player.history.map((entry, index) => (
                <HistoryItem key={index}>
                  <p>
                    <strong>Turn {index + 1}:</strong>{` `}
                    {renderHistoryContent(entry, !!entry.isFinalThrowAttempt)}
                  </p>
                </HistoryItem>
              ))
            )}
          </HistoryScroll>
        </HistoryContainer>
      )}
    </Card>
  );
  };
  
  export default PlayerCard;
  